import { Colours } from '@ecdlink/ui';
import { LeagueType, Tier, TierPercentages } from '../view-clinic-report.types';

const SUPER_LEAGUE_GOLD_THRESHOLD = 8000;
const SUPER_LEAGUE_BAR_MAX = 10000;
const LEAGUE_BAR_MAX = 5000;

export function getClinicLeagueTypeName(clinic?: {
  league?: { leagueTypeName?: string };
  leagues?: Array<{
    league?: {
      startDate?: string;
      endDate?: string;
      leagueType?: { name?: string };
    };
  }>;
}): string | undefined {
  if (clinic?.league?.leagueTypeName) {
    return clinic.league.leagueTypeName;
  }

  const leagues = clinic?.leagues;
  if (!leagues?.length) {
    return undefined;
  }

  const now = Date.now();
  const matching = leagues.find((item) => {
    const start = item?.league?.startDate
      ? new Date(item.league.startDate).getTime()
      : undefined;
    const end = item?.league?.endDate
      ? new Date(item.league.endDate).getTime()
      : undefined;

    if (typeof start === 'number' && typeof end === 'number') {
      return now >= start && now <= end;
    }

    return Boolean(item?.league?.leagueType?.name);
  });

  return (
    matching?.league?.leagueType?.name ?? leagues[0]?.league?.leagueType?.name
  );
}

export function resolveLeagueType(
  maxPointsTotal?: number,
  leagueTypeName?: string
): LeagueType {
  // Prefer max points from the same query as the score: seed data stores
  // LeagueType.MaxPoints as 8000 (Super League) or 3000 (League).
  if ((maxPointsTotal ?? 0) >= SUPER_LEAGUE_GOLD_THRESHOLD) {
    return LeagueType.SuperLeague;
  }

  if ((maxPointsTotal ?? 0) > 0) {
    return LeagueType.League;
  }

  if (leagueTypeName === LeagueType.SuperLeague) {
    return LeagueType.SuperLeague;
  }

  return LeagueType.League;
}

export function getLeagueBarMaxPoints(leagueType: LeagueType): number {
  return leagueType === LeagueType.SuperLeague
    ? SUPER_LEAGUE_BAR_MAX
    : LEAGUE_BAR_MAX;
}

export function calculateTierPercentages(
  leagueType: LeagueType
): TierPercentages {
  const maxPoints = leagueType === LeagueType.SuperLeague ? 10000 : 5000;
  const totalPercentage = 100;

  let bronzePercentage = 0;
  let silverPercentage = 0;
  let goldPercentage = 0;

  if (leagueType === LeagueType.SuperLeague) {
    bronzePercentage = (5000 / maxPoints) * totalPercentage;
    silverPercentage = ((8000 - 5000) / maxPoints) * totalPercentage;
    goldPercentage = ((maxPoints - 8000) / maxPoints) * totalPercentage;
  } else {
    bronzePercentage = (1000 / maxPoints) * totalPercentage;
    silverPercentage = ((3000 - 1000) / maxPoints) * totalPercentage;
    goldPercentage = ((maxPoints - 3000) / maxPoints) * totalPercentage;
  }

  return {
    bronzePercentage,
    silverPercentage,
    goldPercentage,
  };
}

export function getTierDetails(
  teamType: LeagueType,
  points: number
): {
  tierName: Tier;
  tierColor: Colours;
  pointsToNextTier: number;
  nextTier?: 'Silver' | 'Gold';
} {
  const bronzeColour: Colours = 'alertDark';
  const silverColour: Colours = 'textLight';
  const goldColour: Colours = 'tertiary';

  if (teamType === LeagueType.SuperLeague) {
    if (points < 5000) {
      return {
        tierName: Tier.Bronze,
        tierColor: bronzeColour,
        pointsToNextTier: 5000 - points,
        nextTier: 'Silver',
      };
    } else if (points >= 5000 && points <= 8000) {
      return {
        tierName: Tier.Silver,
        tierColor: silverColour,
        pointsToNextTier: 8000 - points,
        nextTier: 'Gold',
      };
    } else {
      return {
        tierName: Tier.Gold,
        tierColor: goldColour,
        pointsToNextTier: 0,
      };
    }
  } else {
    if (points < 1000) {
      return {
        tierName: Tier.Bronze,
        tierColor: bronzeColour,
        pointsToNextTier: 1000 - points,
        nextTier: 'Silver',
      };
    } else if (points >= 1000 && points <= 3000) {
      return {
        tierName: Tier.Silver,
        tierColor: silverColour,
        pointsToNextTier: 3000 - points,
        nextTier: 'Gold',
      };
    } else {
      return {
        tierName: Tier.Gold,
        tierColor: goldColour,
        pointsToNextTier: 0,
      };
    }
  }
}
